import { styled } from "styled-components";

import { PlayIcon, UIButton } from "common/components";
import { useTranslate } from "common/utilities/useTranslate";

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.375s ease;
`;

const StyledImageContainer = styled.div<{ height: string }>`
  width: 100%;
  height: ${({ height }) => height};
  position: relative;
  overflow: hidden;
  border-radius: 0.625rem;
  cursor: pointer;

  &:hover {
    ${StyledImage} {
      transform: translate3d(0, 0, 0) scale(1.2);
    }
  }
`;

const StyledInfo = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  bottom: 0;
  left: 0;
  right: 0;
`;

const StyledPlayButton = styled(UIButton)`
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 32px;
  border-style: solid;
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors.buttonOutlinedText};
  color: white;
  font-size: 0;
  gap: 0;
  height: fit-content;
  padding: 0.8rem;
  transition:
    font-size 0.2s ease,
    gap 0.2s ease;

  &:focus-visible,
  &:hover {
    font-size: 0.8rem;
    gap: 0.5rem;
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.buttonActiveBorder};
    outline: none;
  }
`;

const StyledPlayVideoText = styled.p`
  margin: 0;
`;

const StyledPlayIcon = styled(PlayIcon)`
  padding-left: 2px;
`;

const StyledVideoLengthText = styled.span`
  color: white;
  background-color: rgba(0, 0, 0, 0.6);
  padding: 7px 8px;
  border-radius: 5px;
`;

type VideoCoverProps = {
  height: string;
  image: string;
  onClick: () => void;
  videoLength: string;
  videoName: string;
};

export const VideoCover = ({ height, image, onClick, videoLength, videoName }: VideoCoverProps) => {
  const translate = useTranslate();
  return (
    <StyledImageContainer height={height}>
      <StyledImage src={image} />
      <StyledInfo>
        <StyledPlayButton
          aria-label={`${translate("app.resources.play_video")} ${videoName}`}
          onClick={onClick}
          variant="neutral"
        >
          <StyledPlayIcon width={16} height={16} />
          <StyledPlayVideoText>{translate("app.resources.play_video")}</StyledPlayVideoText>
        </StyledPlayButton>
        <StyledVideoLengthText>{videoLength}</StyledVideoLengthText>
      </StyledInfo>
    </StyledImageContainer>
  );
};
